import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Clock, Trash2, Plus, Edit, Image as ImageIcon, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';
import ImageUpload from '@/components/ImageUpload';

// Move ServiceForm outside to prevent recreation on every render
const ServiceForm = ({ formData, setFormData, onSubmit, isEdit, categories }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div>
      <label className="text-sm font-medium">Type</label>
      <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
        <SelectTrigger data-testid="service-type-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[100]">
          <SelectItem value="service">Service</SelectItem>
          <SelectItem value="good">Good</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <label className="text-sm font-medium">Title</label>
      <Input
        placeholder="e.g., Web Development or Used Bicycle"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        required
        data-testid="service-title-input"
      />
    </div>
    <div>
      <label className="text-sm font-medium">Short Description</label>
      <Textarea
        placeholder="Brief description (shown in cards)..."
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        required
        rows={2}
        data-testid="service-description-input"
      />
    </div>
    <div>
      <label className="text-sm font-medium">Detailed Description (Optional)</label>
      <Textarea
        placeholder="Full details about your offering..."
        value={formData.detailed_description}
        onChange={(e) => setFormData({ ...formData, detailed_description: e.target.value })}
        rows={4}
        data-testid="service-detailed-description-input"
      />
    </div>
    <ImageUpload 
      images={formData.images}
      onImagesChange={(images) => setFormData({ ...formData, images })}
    />
    <div>
      <label className="text-sm font-medium">Time Cost (hours)</label>
      <Input
        type="number"
        step="0.5"
        min="0.5"
        placeholder="e.g., 2"
        value={formData.time_cost}
        onChange={(e) => setFormData({ ...formData, time_cost: e.target.value })}
        required
        data-testid="service-cost-input"
      />
    </div>
    <div>
      <label className="text-sm font-medium">Category</label>
      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
        <SelectTrigger data-testid="service-category-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[100]">
          {categories.map((cat) => (
            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2">
        <MapPin className="w-5 h-5 text-gray-600" />
        <div>
          <Label htmlFor="location-visible" className="font-medium">Show in location-based search</Label>
          <p className="text-xs text-gray-500">Allow users to find this listing based on distance</p>
        </div>
      </div>
      <Switch
        id="location-visible"
        checked={formData.location_visible}
        onCheckedChange={(checked) => setFormData({ ...formData, location_visible: checked })}
        data-testid="location-visible-toggle"
      />
    </div>
    <Button type="submit" className="w-full" data-testid="submit-service-button">
      {isEdit ? 'Update Listing' : `Create ${formData.type === 'service' ? 'Service' : 'Good'}`}
    </Button>
  </form>
);

function MyServices() {
  const { API } = useContext(AppContext);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    detailed_description: '',
    time_cost: '',
    category: 'Other',
    type: 'service',
    images: [],
    location_visible: true
  });

  const categories = ['Education', 'Technology', 'Creative', 'Health', 'Home', 'Other'];

  useEffect(() => {
    fetchMyServices();
  }, []);

  const fetchMyServices = async () => {
    try {
      const response = await axios.get(`${API}/services/my`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/services`, {
        ...formData,
        time_cost: parseFloat(formData.time_cost)
      });
      toast.success('Listing created successfully!');
      setOpen(false);
      setFormData({ title: '', description: '', detailed_description: '', time_cost: '', category: 'Other', type: 'service', images: [], location_visible: true });
      fetchMyServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/services/${editDialog.id}`, {
        ...formData,
        time_cost: formData.time_cost ? parseFloat(formData.time_cost) : undefined
      });
      toast.success('Listing updated successfully!');
      setEditDialog(null);
      setFormData({ title: '', description: '', detailed_description: '', time_cost: '', category: 'Other', type: 'service', images: [], location_visible: true });
      fetchMyServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Listing deleted successfully!');
      fetchMyServices();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  const openEditDialog = (service) => {
    setFormData({
      title: service.title,
      description: service.description,
      detailed_description: service.detailed_description || '',
      time_cost: service.time_cost.toString(),
      category: service.category,
      type: service.type,
      images: service.images || [],
      location_visible: service.location_visible !== undefined ? service.location_visible : true
    });
    setEditDialog(service);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Education': 'bg-blue-100 text-blue-800',
      'Technology': 'bg-purple-100 text-purple-800',
      'Creative': 'bg-pink-100 text-pink-800',
      'Health': 'bg-green-100 text-green-800',
      'Home': 'bg-orange-100 text-orange-800',
      'Other': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['Other'];
  };

  if (loading) {
    return <div className="text-center py-8">Loading listings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="my-services">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">My Listings</h1>
          <p className="text-gray-600">Manage your services and goods offerings</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-service-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Listing
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-service-dialog">
            <DialogHeader>
              <DialogTitle>Create New Listing</DialogTitle>
              <DialogDescription>List a service or good you can provide to the community</DialogDescription>
            </DialogHeader>
            <ServiceForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleCreateService}
              isEdit={false}
              categories={categories}
            />
          </DialogContent>
        </Dialog>
      </div>

      {services.length === 0 ? (
        <Card className="bg-white shadow-lg">
          <CardContent className="py-12">
            <p className="text-center text-gray-500">You haven't created any listings yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <Card key={service.id} className="bg-white shadow-lg" data-testid={`my-service-${service.id}`}>
              <CardHeader>
                {service.images && service.images.length > 0 && (
                  <div className="mb-3 -mt-6 -mx-6">
                    <img 
                      src={service.images[0]} 
                      alt={service.title}
                      className="w-full h-40 object-cover rounded-t-lg"
                    />
                    {service.images.length > 1 && (
                      <Badge className="absolute top-2 right-2 bg-black/60 text-white">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {service.images.length}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="flex gap-2">
                    <Badge className={getCategoryColor(service.category)}>{service.category}</Badge>
                    <Badge variant="outline" className="capitalize">{service.type}</Badge>
                  </div>
                  <div className="flex items-center gap-1 text-purple-600 font-semibold">
                    <Clock className="w-4 h-4" />
                    <span>{service.time_cost}h</span>
                  </div>
                </div>
                <CardTitle className="mt-2">{service.title}</CardTitle>
                <CardDescription className="text-sm">Created {new Date(service.created_at).toLocaleDateString()}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">{service.description}</p>
                {service.detailed_description && (
                  <p className="text-gray-500 text-xs mt-2 line-clamp-2">{service.detailed_description}</p>
                )}
              </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => openEditDialog(service)}
                  data-testid={`edit-service-${service.id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDeleteService(service.id)}
                  data-testid={`delete-service-${service.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Listing</DialogTitle>
              <DialogDescription>Update your service or good details</DialogDescription>
            </DialogHeader>
            <ServiceForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleUpdateService}
              isEdit={true}
              categories={categories}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default MyServices;